import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tag, Plus, Pencil, Trash } from "@phosphor-icons/react";

export default function AdminCategoriesPage() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) { console.error("Failed to fetch categories:", error); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  usePolling(fetchCategories, 15000);

  const handleSubmit = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/categories`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Category created");
      }
      setDialogOpen(false); setEditingCategory(null); setFormData({ name: "", description: "" }); fetchCategories();
    } catch (error) { toast.error(error.response?.data?.detail || "Operation failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Category deleted"); fetchCategories();
    } catch (error) { toast.error("Failed to delete category"); }
  };

  return (
    <>
      <SEO title="Categories Management" description="Manage business categories available across BCH chapters." path="/admin/categories" noIndex />
      <div className="space-y-6 animate-fadeInUp" data-testid="admin-categories-page">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl md:text-4xl text-primary">Categories</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-bch-teal hover:bg-bch-teal-dark rounded-xl" onClick={() => { setEditingCategory(null); setFormData({ name: "", description: "" }); }}>
                <Plus className="w-5 h-5 mr-2" /> Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-[var(--border-color)]">
              <DialogHeader><DialogTitle className="text-primary">{editingCategory ? "Edit Category" : "Create Category"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-secondary">Category Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="IT Services" />
                </div>
                <div className="space-y-2">
                  <Label className="text-secondary">Description</Label>
                  <Input value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="Information Technology services" />
                </div>
              </div>
              <DialogFooter><Button onClick={handleSubmit} className="bg-bch-gold hover:bg-bch-gold-dark text-black rounded-xl">{editingCategory ? "Update" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <div key={cat.id} className="bg-card border border-[var(--border-color)] rounded-2xl p-4 flex items-center justify-between hover:border-amber-300/40 transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Tag className="w-4 h-4 text-white" weight="fill" />
                  </div>
                  <span className="text-primary font-medium">{cat.name}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditingCategory(cat); setFormData({ name: cat.name, description: cat.description }); setDialogOpen(true); }} className="p-2 hover:bg-surface rounded-xl transition-colors">
                    <Pencil className="w-4 h-4 text-secondary" />
                  </button>
                  <button onClick={() => handleDelete(cat.id)} className="p-2 hover:bg-red-500/10 rounded-xl transition-colors">
                    <Trash className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
